import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@/lib/utils";
import {
  ALERT_MAX_H_CLASS,
  SHEET_ANIM_CENTERED,
  SHEET_OVERLAY_ALERT_CLASS,
} from "@/components/ui/sheetChrome";
import { buttonVariants } from "@/components/ui/button";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      SHEET_OVERLAY_ALERT_CLASS,
      className,
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

/**
 * Los avisos de confirmar/cancelar van CENTRADOS, también en el teléfono.
 *
 * Fueron hoja inferior una temporada, para que se parecieran al resto. El problema
 * es dónde salen: un aviso casi siempre aparece ENCIMA de una hoja ya abierta, y
 * dos hojas compartiendo el mismo borde de abajo, el mismo redondeo y la misma
 * subida no se leen como «te interrumpo con una pregunta», se leen como que la
 * hoja de debajo se ha vuelto a dibujar. Justo lo que hay que evitar.
 *
 * Centrado, además, se contesta antes: la X que abre el aviso está arriba a la
 * derecha y los botones quedaban abajo del todo — en una hoja a pantalla completa,
 * eso es cruzar el teléfono entero. En el medio el viaje es la mitad, y es donde
 * ya está mirando el ojo.
 *
 * Sin tirador: esto ya no es una hoja, y un tirador que no arrastra es mentira.
 * Se sale con Escape, tocando el velo o con Cancelar, que está a la vista.
 *
 * Pequeño A PROPÓSITO. Pequeño y centrado se lee como aviso; pequeño y abajo se
 * leía como otra hoja más. El tamaño nunca fue el problema, era el sitio.
 */
const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, onOpenAutoFocus, ...props }, ref) => {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={createTouchOpenAutoFocusHandler(onOpenAutoFocus)}
        className={cn(
          // `[&>*]:min-w-0` por lo mismo que en dialog.tsx: los hijos de un grid
          // arrancan en `min-width: auto` y un texto largo sin cortes ensancha la
          // ventana hasta salirse. Ver el comentario largo allí.
          "fixed z-50 grid [&>*]:min-w-0 gap-4 border bg-background p-5 shadow-lg",
          // Centrada en TODOS los tamaños. Los desplazamientos del zoom de
          // SHEET_ANIM_CENTERED (-50% / -48%) son los mismos que este translate: si
          // no coincidieran, el primer fotograma saldría de (0,0) y la tarjeta
          // cruzaría la pantalla en diagonal antes de colocarse.
          "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
          // Compacta en el teléfono; en tableta y escritorio, la de siempre.
          "w-[calc(100%-3rem)] max-w-[360px] md:max-w-lg md:w-[calc(100%-2.5rem)] md:p-6",
          ALERT_MAX_H_CLASS,
          "overflow-y-auto overscroll-contain",
          // Literal a propósito: Tailwind lee el código fuente, así que una clase
          // construida en tiempo de ejecución no se generaría.
          "rounded-[24px]",
          SHEET_ANIM_CENTERED,
          className,
        )}
        {...props}
      >
        {/* Ya no hay Cancel oculto: existía para que la franja del tirador tuviera
            a quién pulsar. Sin tirador, el único Cancel es el del consumidor, que
            es justo el que Radix enfoca al abrir. */}
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-left", className)} {...props} />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse md:flex-row md:justify-end md:space-x-2", className)} {...props} />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold", className)} {...props} />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    /* `md:mt-0` y no `sm:mt-0`: el pie se pone en fila en `md`, así que entre 640
       y 767 px los botones seguían apilados pero ya sin separación — se tocaban. */
    className={cn(buttonVariants({ variant: "outline" }), "mt-2 md:mt-0", className)}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
